import { useMemo, type ReactElement, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  detectCurrentPlatform,
  formatBytes,
  messageForError,
  platformLabel,
} from '@proctoring/api-client';
import type { DownloadAsset, DownloadPlatform, DownloadVersion } from '@proctoring/shared';
import { useApi } from '../auth/AuthProvider.js';
import { SUPPORT_EMAIL } from '../api/config.js';

/**
 * The desktop-installer download surface.
 *
 * The rule this component exists to enforce: **never render a button that cannot be
 * clicked**. The state it replaced was three permanently `disabled` buttons captioned
 * "Download links are not yet published in this environment" — which reads, to a candidate
 * with an interview in an hour, as "the thing you need is broken and there is nothing you
 * can do". Every state below either hands over a working link or says plainly that there
 * is none and where to go instead.
 */

const PLATFORM_ORDER: readonly DownloadPlatform[] = ['win', 'mac', 'linux'];

/** Formats that are an installer a human runs, in the order they should be offered. */
const INSTALLER_FORMATS: Record<DownloadPlatform, readonly string[]> = {
  win: ['exe'],
  mac: ['dmg', 'zip'],
  linux: ['deb', 'rpm', 'appimage'],
};

const FORMAT_LABELS: Record<string, string> = {
  exe: 'Installer (.exe)',
  dmg: 'Disk image (.dmg)',
  zip: 'Zip archive (.zip)',
  deb: 'Debian/Ubuntu (.deb)',
  rpm: 'Fedora/RHEL (.rpm)',
  appimage: 'AppImage',
};

const ARCH_LABELS: Record<string, string> = {
  arm64: 'Apple silicon / ARM64',
  x64: 'Intel / x64',
  // The unmarked electron-builder slice; on macOS that is the Intel build.
  universal: 'Intel / x64',
};

export function installersFor(
  version: DownloadVersion,
  platform: DownloadPlatform,
): DownloadAsset[] {
  const wanted = INSTALLER_FORMATS[platform];
  return version.assets
    .filter((asset) => asset.platform === platform && wanted.includes(asset.format))
    .sort((a, b) => wanted.indexOf(a.format) - wanted.indexOf(b.format));
}

/** One platform's offer: its newest build, and which version that build came from. */
export interface PlatformOffer {
  platform: DownloadPlatform;
  version: DownloadVersion;
  assets: DownloadAsset[];
}

/**
 * The newest build for each platform, which is not the same as "the newest version".
 *
 * Publishing is per platform: a tag whose Linux leg failed advances mac and win while Linux
 * keeps serving the tag before it, so the manifest legitimately holds mac on 0.2.0 and linux
 * on 0.1.0 at the same time. Reading only `versions[0]` would drop Linux off the page
 * entirely — the candidate would be told there is no Linux build when there is a perfectly
 * good one.
 *
 * Deliberately *not* surfaced as a warning anywhere. A candidate installing Linux 0.1.0 an
 * hour before an interview gains nothing from knowing macOS is on 0.2.0; the version is
 * shown as a fact about what they are downloading, not as a problem for them to solve.
 *
 * @param versions Newest first, as the manifest returns them.
 */
export function newestPerPlatform(versions: readonly DownloadVersion[]): PlatformOffer[] {
  const offers: PlatformOffer[] = [];
  for (const platform of PLATFORM_ORDER) {
    for (const version of versions) {
      const assets = installersFor(version, platform);
      if (assets.length > 0) {
        offers.push({ platform, version, assets });
        break;
      }
    }
  }
  return offers;
}

export interface DownloadPanelProps {
  /** Rendered under the list — the deep link back into an already-installed app. */
  footer?: ReactNode;
  /**
   * Injectable so tests need no `navigator` stubbing and so a story can force a platform.
   * `undefined` means "ask the browser".
   */
  detectedPlatform?: DownloadPlatform | null;
}

export function DownloadPanel({ footer, detectedPlatform }: DownloadPanelProps): ReactElement {
  const api = useApi();
  const query = useQuery({
    queryKey: ['downloads', 'manifest'],
    queryFn: ({ signal }) => api.downloadManifest(signal),
    // The manifest changes a few times a month; refetching it on every focus is noise.
    staleTime: 5 * 60_000,
  });

  const detected = useMemo(
    () =>
      detectedPlatform === undefined
        ? (detectCurrentPlatform()?.platform ?? null)
        : detectedPlatform,
    [detectedPlatform],
  );

  const offers = useMemo(() => newestPerPlatform(query.data?.versions ?? []), [query.data]);

  return (
    <section className="panel mt-6 p-5" aria-labelledby="desktop-heading">
      <h2 id="desktop-heading" className="text-sm font-semibold text-session-text">
        You need the desktop app
      </h2>
      <p className="mt-2 text-sm text-session-muted">
        The interview runs in the Proctoring desktop app, which provides the coding environment, the
        video call and the monitored session. It cannot be taken in a browser. Install it ahead of
        time and run the full system check there.
      </p>

      {query.isPending ? (
        <p className="mt-4 text-sm text-session-muted" data-testid="downloads-loading">
          Checking for the latest version…
        </p>
      ) : null}

      {query.error ? (
        <p role="alert" className="form-error mt-4" data-testid="downloads-error">
          Could not load the download list ({messageForError(query.error)}). Email <SupportLink />{' '}
          and we will send you an installer directly.
        </p>
      ) : null}

      {/*
        The honest empty state. No greyed-out buttons, no "coming soon": a candidate who
        needs the app today is told, in one sentence, that the link does not exist yet and
        given the one action that actually resolves it.
      */}
      {query.data && offers.length === 0 ? (
        <p className="mt-4 text-sm text-session-muted" data-testid="downloads-empty">
          No installer has been published yet. Email <SupportLink /> with your interview time and we
          will send you one before your session.
        </p>
      ) : null}

      {offers.length > 0 ? (
        <div className="mt-4" data-testid="downloads-list">
          {offers.map((offer) => (
            <PlatformRow
              key={offer.platform}
              offer={offer}
              recommended={offer.platform === detected}
            />
          ))}
          <ChecksumFooter offers={offers} />
          {detected ? <InstallSteps platform={detected} /> : null}
        </div>
      ) : null}

      {footer ? <div className="mt-3 text-xs text-session-dim">{footer}</div> : null}
    </section>
  );
}

function SupportLink(): ReactElement {
  return (
    <a href={`mailto:${SUPPORT_EMAIL}`} className="text-session-accent underline">
      {SUPPORT_EMAIL}
    </a>
  );
}

interface PlatformRowProps {
  offer: PlatformOffer;
  recommended: boolean;
}

function PlatformRow({ offer, recommended }: PlatformRowProps): ReactElement {
  const api = useApi();
  const { platform, version, assets } = offer;
  return (
    <div className="mt-4 first:mt-0" data-testid={`downloads-${platform}`}>
      <p className="text-xs font-semibold uppercase tracking-wide text-session-dim">
        {platformLabel(platform)}
        {/*
          The version belongs on the platform row, not on a single footer line: each
          platform can be on a different one. Stated plainly, with no comparison to the
          others — "your build is older than the Mac build" is not a candidate's problem
          to act on, and a warning there would only make a working download look broken.
        */}
        <span className="ml-2 font-normal normal-case tracking-normal text-session-muted">
          version {version.version}
        </span>
        {recommended ? (
          <span
            data-testid="downloads-recommended"
            className="ml-2 rounded-full bg-severity-info/10 px-2 py-0.5 text-[10px] font-medium normal-case tracking-normal text-severity-info"
          >
            Detected on this computer
          </span>
        ) : null}
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        {assets.map((asset) => (
          <a
            key={asset.filename}
            href={api.downloadUrl(asset)}
            // The response carries `content-disposition: attachment`, so the download
            // happens either way; the attribute keeps the filename right if a proxy ever
            // strips the header.
            download={asset.filename}
            className={recommended ? 'btn-primary' : 'btn-secondary'}
          >
            {FORMAT_LABELS[asset.format] ?? asset.format} · {ARCH_LABELS[asset.arch] ?? asset.arch}{' '}
            <span className="text-session-dim">({formatBytes(asset.sizeBytes)})</span>
          </a>
        ))}
      </div>
    </div>
  );
}

/**
 * Publication dates and checksums for whatever is being offered above.
 *
 * Plural because platforms drift: with a broken Linux leg this footer names both the version
 * mac/win are on and the older one Linux is still serving. The per-platform version a
 * candidate actually needs is on their own row; this is the provenance line beneath it.
 *
 * Shown because these builds may be unsigned (the release workflow succeeds without signing
 * certificates), and an unsigned installer that the OS warns about needs *some* integrity
 * signal a candidate can act on. The full hash is in a `<details>` rather than inline: the
 * ten people who verify it need it exactly, and everyone else should not be shown 64 hex
 * characters on the way to a download button.
 */
function ChecksumFooter({ offers }: { offers: readonly PlatformOffer[] }): ReactElement {
  // Only the files actually offered above, deduplicated: the same version can back more
  // than one platform row, and listing its checksums twice would be noise. Assets from
  // versions nobody is being offered are omitted entirely — a hash for a build that has no
  // button is something to verify against nothing.
  const seen = new Set<string>();
  const withHashes: DownloadAsset[] = [];
  for (const offer of offers) {
    for (const asset of offer.assets) {
      if (asset.sha256 === null || seen.has(asset.url)) continue;
      seen.add(asset.url);
      withHashes.push(asset);
    }
  }

  // Newest-first, deduplicated: usually one line, two or three while a platform's build leg
  // is broken and that platform is still serving an older tag.
  const published = [...new Map(offers.map((o) => [o.version.version, o.version])).values()];

  return (
    <div className="mt-5 border-t border-session-border pt-3">
      <p className="text-xs text-session-dim" data-testid="downloads-version">
        {published
          .map(
            (version) =>
              `Version ${version.version} · published ${new Date(version.releasedAt).toLocaleDateString()}`,
          )
          .join(' — ')}
      </p>
      {withHashes.length > 0 ? (
        <details className="mt-2">
          <summary className="cursor-pointer text-xs text-session-muted">
            Verify your download (SHA-256)
          </summary>
          <dl className="mt-2 space-y-1" data-testid="downloads-checksums">
            {withHashes.map((asset) => (
              <div key={asset.filename}>
                <dt className="text-xs text-session-muted">{asset.filename}</dt>
                <dd className="session-tabular-nums break-all text-[11px] text-session-dim">
                  {asset.sha256}
                </dd>
              </div>
            ))}
          </dl>
        </details>
      ) : null}
    </div>
  );
}

/**
 * What to do after the download finishes.
 *
 * These builds are not signed by Apple or Microsoft, so the OS actively resists them:
 * macOS reports the app "is damaged and can't be opened", which reads as a corrupt file
 * rather than an unsigned one, and Windows shows a SmartScreen block. A candidate who is
 * minutes from an interview deletes it and emails the recruiter. The steps existed only in
 * the desktop app's README, which no candidate will ever open.
 *
 * The screen-recording step is called out separately because it is the single most common
 * support issue: macOS reads that permission once at process start, so an app that was
 * already running when the grant was made keeps failing preflight until it is restarted.
 */
const INSTALL_STEPS: Readonly<Record<DownloadPlatform, readonly string[]>> = {
  mac: [
    'Open the .dmg and drag Proctoring to Applications.',
    'The first time you open it, macOS may say the app is damaged. It is not — it just is not signed yet. Right-click the app, choose Open, then Open again.',
    'Allow Camera and Microphone when asked.',
    'Open System Settings → Privacy & Security → Screen & System Audio Recording and switch Proctoring on.',
    'Quit the app and open it again. Screen recording only takes effect on a fresh start, so this step is not optional.',
  ],
  win: [
    'Run the .exe installer.',
    'Windows SmartScreen may warn that the publisher is unknown. Choose More info, then Run anyway.',
    'Allow Camera and Microphone when asked.',
  ],
  linux: [
    'Install the .deb (sudo apt install ./proctoring_*.deb) or the .rpm (sudo dnf install ./proctoring-*.rpm).',
    'For the AppImage, make it executable first: chmod +x Proctoring-*.AppImage',
    'Allow Camera and Microphone when asked.',
  ],
};

function InstallSteps({ platform }: { platform: DownloadPlatform }): ReactElement {
  return (
    <details className="mt-4 border-t border-session-border pt-3" data-testid="install-steps" open>
      <summary className="cursor-pointer text-sm text-session-text">
        After downloading — {platformLabel(platform)}
      </summary>
      <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-session-muted">
        {INSTALL_STEPS[platform].map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
      <p className="mt-2 text-sm text-session-muted">
        Stuck? Email <SupportLink /> and we will help before your interview.
      </p>
    </details>
  );
}
